import React, { useState, useEffect } from 'react';
import { Table, Card, Tag, Button, Modal, Form, Select, Input, Image, Popconfirm, Space, Typography, message, Upload } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SmileOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const TYPE_OPTIONS = [
  { value: 'static', label: '静态' },
  { value: 'gif', label: '动态' },
  { value: '3d', label: '3D' },
];
const TYPE_LABELS = {
  static: { label: '静态', color: 'blue' },
  gif: { label: '动态', color: 'purple' },
  '3d': { label: '3D', color: 'green' },
};

function StickerManagePage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [coverUrl, setCoverUrl] = useState('');
  const [contentUrl, setContentUrl] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { list } = await api.get('/admin/sticker');
      setList(list);
    } catch (e) {
      message.error('加载表情列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: 'static', status: 1, sort: 0 });
    setCoverUrl('');
    setContentUrl('');
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    form.setFieldsValue({ name: r.name, type: r.type, sort: r.sort, status: r.status });
    setCoverUrl(r.cover || '');
    setContentUrl(r.content || '');
    setModalOpen(true);
  };

  // 通用上传：调用后台 /api/v1/upload/image，成功回填 url
  const doUpload = (file, onDone) => {
    const fd = new FormData();
    fd.append('file', file);
    api.post('/upload/image', fd)
      .then((res) => onDone(res.url))
      .catch(() => message.error('上传失败'));
  };
  const uploadProps = (setUrl) => ({
    showUploadList: false,
    accept: 'image/*',
    beforeUpload: (file) => {
      doUpload(file, (url) => { setUrl(url); message.success('上传成功'); });
      return false; // 阻止 antd 默认上传，由我们自定义处理
    },
  });

  const handleOk = async () => {
    try {
      const v = await form.validateFields();
      if (!coverUrl || !contentUrl) { message.error('请先上传封面图和内容图'); return; }
      setSubmitting(true);
      const payload = { ...v, cover: coverUrl, content: contentUrl };
      if (editing) {
        await api.put(`/admin/sticker/${editing.id}`, payload);
        message.success('已更新');
      } else {
        await api.post('/admin/sticker', payload);
        message.success('已添加');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e.errorFields) return;
      message.error(e.error || '保存失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/sticker/${id}`);
      message.success('已删除');
      load();
    } catch (e) {
      message.error('删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '封面', dataIndex: 'cover', width: 80,
      render: (v) => (v ? <Image src={v} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} /> : '-'),
    },
    { title: '名称', dataIndex: 'name', width: 160 },
    {
      title: '类型', dataIndex: 'type', width: 90,
      render: (v) => <Tag color={TYPE_LABELS[v]?.color}>{TYPE_LABELS[v]?.label || v}</Tag>,
    },
    {
      title: '内容', dataIndex: 'content', width: 80,
      render: (v) => (v ? <Image src={v} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} /> : '-'),
    },
    { title: '排序', dataIndex: 'sort', width: 80 },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v) => (v === 1 ? <Tag color="green">上架</Tag> : <Tag>下架</Tag>),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 170, render: (v) => (v ? new Date(v).toLocaleString() : '-') },
    {
      title: '操作', width: 140, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该表情?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}><SmileOutlined /> 表情管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: '#07C160' }}>
          新增表情
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title={editing ? '编辑表情' : '新增表情'}
        open={modalOpen}
        onOk={handleOk}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="表情名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="例如：熊猫头" />
          </Form.Item>
          <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select options={TYPE_OPTIONS} />
          </Form.Item>
          <Form.Item label="封面图（表情广场功能栏显示）" required>
            <Space>
              <Upload {...uploadProps(setCoverUrl)}>
                <Button icon={<PlusOutlined />}>上传封面</Button>
              </Upload>
              {coverUrl
                ? <Image src={coverUrl} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
                : <Text type="secondary">未上传</Text>}
            </Space>
          </Form.Item>
          <Form.Item label="内容图（聊天里发送的表情）" required>
            <Space>
              <Upload {...uploadProps(setContentUrl)}>
                <Button icon={<PlusOutlined />}>上传内容</Button>
              </Upload>
              {contentUrl
                ? <Image src={contentUrl} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
                : <Text type="secondary">未上传</Text>}
            </Space>
          </Form.Item>
          <Form.Item name="sort" label="排序（越小越靠前）" rules={[{ required: true, message: '请输入排序' }]}>
            <Input type="number" placeholder="0" />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select options={[{ value: 1, label: '上架' }, { value: 0, label: '下架' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default StickerManagePage;
